import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ProviderBidsTab = ({
  myBids,
  onWithdrawBid,
  onDeleteBid,
  onClearAllBids,
  onViewJobDetails,
  onMessageClient,
  getStatusColor
}) => {
  const { t } = useTranslation();
  if (myBids.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
        <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('providerBids.noBids')}</h3>
        <p className="text-gray-500 mb-6">{t('providerBids.noBidsHint')}</p>
        <Link
          to="/jobs"
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {t('providerBids.browseJobs')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Clear All Button */}
      {myBids.filter(b => b.status === 'pending').length > 0 && (
        <div className="flex justify-end mb-4">
          <button
            onClick={onClearAllBids}
            className="text-sm text-red-600 hover:text-red-700 hover:underline"
          >
            {t('providerBids.clearAllPending')}
          </button>
        </div>
      )}
      
      {myBids.map((bid) => (
        <div
          key={bid.id}
          className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition"
          data-testid={`bid-card-${bid.id}`}
        >
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 text-lg">
                  {bid.job_title || t('providerBids.jobPosting')}
                </h3>
                {/* Job Order Code */}
                {bid.job_order_code && (
                  <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs px-2 py-0.5 rounded-full font-mono font-medium" title="Job Order Code">
                    #JO-{bid.job_order_code}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {t('providerBids.postedBy')} <span className="font-medium text-gray-700">{bid.client_name || t('providerBids.client')}</span>
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(bid.status)}`}>
              {t('bookingsList.statuses.' + bid.status, bid.status?.charAt(0).toUpperCase() + bid.status?.slice(1))}
            </span>
          </div>
          
          {/* Job Schedule Info */}
          {(bid.job_preferred_date || bid.job_preferred_start_time || bid.job_deadline) && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-xs text-orange-700 font-semibold mb-2">{t('providerBids.jobSchedule')}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {bid.job_preferred_date && (
                  <span className="flex items-center gap-1 text-gray-700">
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {new Date(bid.job_preferred_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
                {(bid.job_preferred_start_time || bid.job_preferred_end_time) && (
                  <span className="flex items-center gap-1 text-gray-700">
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {bid.job_preferred_start_time && bid.job_preferred_end_time 
                      ? `${bid.job_preferred_start_time} - ${bid.job_preferred_end_time}`
                      : bid.job_preferred_start_time || bid.job_preferred_end_time}
                  </span>
                )}
                {bid.job_deadline && !bid.job_preferred_date && (
                  <span className="flex items-center gap-1 text-gray-700">
                    <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    {t('providerBids.deadline')} {new Date(bid.job_deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('providerBids.yourBid')}</p>
              <p className="text-lg font-bold text-blue-600">₱{bid.bid_amount?.toLocaleString()}</p>
              {bid.estimated_duration && (
                <p className="text-xs text-gray-500 mt-1">{t('providerBids.duration')} {bid.estimated_duration}</p>
              )}
              {bid.proposed_time && (
                <p className="text-xs text-gray-500">{t('providerBids.start')} {bid.proposed_time}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('providerBids.jobBudget')}</p>
              <p className="text-lg font-semibold text-gray-700">
                {bid.job_budget ? `₱${bid.job_budget.toLocaleString()}` : t('providerBids.negotiable')}
              </p>
            </div>
          </div>
          
          {bid.message && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t('providerBids.yourMessage')}</p>
              <p className="text-sm text-gray-700 line-clamp-2">{bid.message}</p>
            </div>
          )}
          
          <div className="flex gap-2 pt-3 border-t border-gray-100">
            <button
              onClick={() => onViewJobDetails(bid)}
              className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition text-sm"
              data-testid={`view-job-btn-${bid.id}`}
            >
              {t('providerBids.jobDetails')}
            </button>
            <button
              onClick={() => onMessageClient(bid)}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition text-sm"
              data-testid={`message-client-btn-${bid.id}`}
            >
              {t('providerBids.message')}
            </button>
            {bid.status === 'pending' && (
              <button
                onClick={() => onWithdrawBid(bid.id)}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium transition text-sm"
              >
                {t('providerBids.withdraw')}
              </button>
            )}
            {bid.status === 'rejected' && (
              <button
                onClick={() => onDeleteBid(bid.id)}
                className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium transition text-sm"
                data-testid={`delete-bid-${bid.id}`}
              >
                {t('providerBids.delete')}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProviderBidsTab;
